"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  dimensionLabels,
  questions,
  TEST_VERSION,
} from "../lib/content.ts";
import { publicFigures as figures } from "../lib/public-figures.ts";
import { randomToken } from "../lib/security.ts";
import {
  DIMENSIONS,
  type AnswerMap,
  type PublicResult,
} from "../lib/types.ts";

type Phase = "intro" | "quiz" | "submitting" | "result";

type DraftState = {
  testVersion: number;
  attemptToken: string;
  accessToken: string;
  answers: AnswerMap;
  currentIndex: number;
};

const DRAFT_KEY = "mingjing_draft_v1";

function readSource() {
  if (typeof window === "undefined") return "direct";
  const parameters = new URLSearchParams(window.location.search);
  return (
    parameters.get("source") ??
    parameters.get("utm_source") ??
    "direct"
  ).slice(0, 64);
}

function track(
  eventName: string,
  properties: Record<string, unknown> = {},
  publicToken?: string,
) {
  void fetch("/api/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      eventName,
      publicToken,
      source: readSource(),
      properties,
    }),
    keepalive: true,
  }).catch(() => undefined);
}

function createDraft(): DraftState {
  return {
    testVersion: TEST_VERSION,
    attemptToken: randomToken(24),
    accessToken: randomToken(32),
    answers: {},
    currentIndex: 0,
  };
}

function readDraft(): DraftState {
  if (typeof window === "undefined") return createDraft();
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(DRAFT_KEY) ?? "null",
    ) as DraftState | null;
    if (
      saved?.testVersion === TEST_VERSION &&
      typeof saved.attemptToken === "string" &&
      typeof saved.accessToken === "string"
    ) {
      return {
        ...saved,
        currentIndex: Math.min(
          questions.length - 1,
          Math.max(0, saved.currentIndex ?? 0),
        ),
      };
    }
  } catch {
    // A corrupted device-local draft should never block a new test.
  }
  return createDraft();
}

function saveDraft(draft: DraftState) {
  window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

function FigureMedallion({
  figure,
  size = "medium",
}: {
  figure: Pick<(typeof figures)[number], "color" | "monogram">;
  size?: "small" | "medium" | "large";
}) {
  return (
    <div
      className={`figure-medallion figure-medallion--${size}`}
      style={{ "--figure-color": figure.color } as React.CSSProperties}
      aria-hidden="true"
    >
      <span>{figure.monogram}</span>
    </div>
  );
}

function BrandMark() {
  return (
    <a className="brand" href="/" aria-label="明镜人格测试首页">
      <span className="brand__seal">鉴</span>
      <span>
        <strong>明镜</strong>
        <small>历史人格志</small>
      </span>
    </a>
  );
}

function Intro({
  hasDraft,
  onStart,
  onContinue,
}: {
  hasDraft: boolean;
  onStart: () => void;
  onContinue: () => void;
}) {
  return (
    <main>
      <section className="hero">
        <nav className="topbar">
          <BrandMark />
          <span className="topbar__note">12 位人物原型 · 14 道情境题</span>
        </nav>

        <div className="hero__ornament" aria-hidden="true">
          <span>观</span>
          <span>心</span>
          <span>知</span>
          <span>人</span>
        </div>

        <div className="hero__content">
          <p className="kicker">人格为镜，照见千年知己</p>
          <h1>
            你在历史长河里，
            <br />
            <em>最像谁？</em>
          </h1>
          <p className="hero__lede">
            不是知识问答，也没有标准答案。我们从你面对选择、关系与风险的方式，
            寻找最接近你的历史人物。
          </p>

          <div className="hero__actions">
            <button className="button button--primary" onClick={onStart}>
              开始照见自己
              <span aria-hidden="true">→</span>
            </button>
            {hasDraft ? (
              <button className="button button--ghost" onClick={onContinue}>
                继续上次答题
              </button>
            ) : null}
          </div>

          <div className="trust-row" aria-label="测试特点">
            <span>约 3 分钟</span>
            <span>基础结果免费</span>
            <span>无需登录</span>
          </div>
        </div>

        <div className="hero__figure-stack" aria-hidden="true">
          {figures.slice(0, 5).map((figure, index) => (
            <div
              className="hero__figure"
              key={figure.slug}
              style={
                {
                  "--stack-index": index,
                  "--figure-color": figure.color,
                } as React.CSSProperties
              }
            >
              <span>{figure.monogram}</span>
              <small>{figure.name}</small>
            </div>
          ))}
        </div>
      </section>

      <section className="method section-shell">
        <div className="section-heading">
          <p className="kicker">不是贴标签，是看见你的选择方式</p>
          <h2>六面人格，一份有来由的答案</h2>
          <p>
            每个选项都会映射到六个行为维度，再与经过区分度检查的人物画像稳定匹配。
            同样的答案，永远得到同样的结果。
          </p>
        </div>
        <div className="dimension-grid">
          {[
            ["决", "决断力", "你如何在不完整信息里做决定"],
            ["谋", "谋略性", "你如何规划、推演和权衡"],
            ["志", "理想主义", "你愿意为何种价值坚持"],
            ["势", "社交影响", "你如何理解并带动他人"],
            ["险", "风险偏好", "你如何面对机会与不确定"],
            ["序", "秩序倾向", "你如何看待规则、结构与纪律"],
          ].map(([mark, label, description]) => (
            <article className="dimension-card" key={label}>
              <span className="dimension-card__mark">{mark}</span>
              <h3>{label}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="figures-section">
        <div className="section-shell">
          <div className="section-heading section-heading--light">
            <p className="kicker">首发人物谱</p>
            <h2>不是八选一，是十二种截然不同的人生解法</h2>
            <p>
              从谋略者、改革者到表达者与远航者。人物不是简单的好坏标签，
              每一种力量都伴随自己的代价。
            </p>
          </div>
          <div className="figure-rail">
            {figures.map((figure) => (
              <article className="figure-card" key={figure.slug}>
                <FigureMedallion figure={figure} />
                <div>
                  <p>{figure.era} · {figure.role}</p>
                  <h3>{figure.name}</h3>
                  <div className="tag-line">
                    {figure.labels.slice(0, 2).map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="sample section-shell">
        <div className="sample__card">
          <div>
            <p className="kicker">你会得到什么</p>
            <h2>免费先看人物与核心判断，再决定是否深入</h2>
            <ul className="feature-list">
              <li><span>01</span>最接近的历史人物与前三候选</li>
              <li><span>02</span>六维人格轮廓与三个核心标签</li>
              <li><span>03</span>可直接保存的小红书比例结果卡</li>
              <li><span>04</span>支付或兑换解锁专属历史人格志</li>
            </ul>
          </div>
          <div className="sample__result" aria-hidden="true">
            <span className="sample__label">示例结果</span>
            <FigureMedallion figure={figures[2]} size="large" />
            <small>你最接近</small>
            <strong>王阳明</strong>
            <p>你不等世界给出答案，行动本身就是求证。</p>
            <div>
              {figures[2].labels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="section-shell">
          <p>十四次选择之后</p>
          <h2>与一个跨越时间的人，重新认识自己。</h2>
          <button className="button button--paper" onClick={onStart}>
            免费开始测试
            <span aria-hidden="true">→</span>
          </button>
          <small>
            本测试仅供娱乐，不属于专业心理测评，不作为医疗、职业或人生决策依据。
          </small>
        </div>
      </section>
    </main>
  );
}

function Quiz({
  draft,
  setDraft,
  onBackToIntro,
  onSubmit,
}: {
  draft: DraftState;
  setDraft: React.Dispatch<React.SetStateAction<DraftState>>;
  onBackToIntro: () => void;
  onSubmit: () => void;
}) {
  const [locked, setLocked] = useState(false);
  const question = questions[draft.currentIndex];
  const selected = draft.answers[question.id];
  const isLast = draft.currentIndex === questions.length - 1;
  const answeredCount = Object.keys(draft.answers).length;
  const progress = ((draft.currentIndex + 1) / questions.length) * 100;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const chooseOption = (optionId: string) => {
    if (locked) return;
    const nextAnswers = { ...draft.answers, [question.id]: optionId };
    setDraft((current) => ({ ...current, answers: nextAnswers }));
    track("question_answered", {
      questionId: question.id,
      questionIndex: draft.currentIndex + 1,
    });
    if (answeredCount < Math.ceil(questions.length / 2) && Object.keys(nextAnswers).length >= Math.ceil(questions.length / 2)) {
      track("test_progress_50");
    }

    if (!isLast) {
      setLocked(true);
      timeoutRef.current = setTimeout(() => {
        setDraft((current) => ({
          ...current,
          currentIndex: Math.min(
            questions.length - 1,
            current.currentIndex + 1,
          ),
        }));
        setLocked(false);
      }, 280);
    }
  };

  return (
    <main className="quiz-shell">
      <header className="quiz-header">
        <BrandMark />
        <button className="text-button" onClick={onBackToIntro}>
          暂时离开
        </button>
      </header>

      <div className="progress-track" aria-label={`第 ${draft.currentIndex + 1} 题，共 ${questions.length} 题`}>
        <span style={{ width: `${progress}%` }} />
      </div>

      <section className="question-stage" key={question.id}>
        <div className="question-meta">
          <span>
            {String(draft.currentIndex + 1).padStart(2, "0")}
            <small>/ {questions.length}</small>
          </span>
          <p>{question.eyebrow}</p>
        </div>
        <h1>{question.text}</h1>
        <p className="question-hint">凭第一反应选择，没有更正确的答案</p>

        <div className="option-list" role="radiogroup" aria-label={question.text}>
          {question.options.map((option, index) => (
            <button
              className={`option ${selected === option.id ? "option--selected" : ""}`}
              key={option.id}
              role="radio"
              aria-checked={selected === option.id}
              onClick={() => chooseOption(option.id)}
            >
              <span className="option__letter">
                {String.fromCharCode(65 + index)}
              </span>
              <span>{option.text}</span>
              <span className="option__check" aria-hidden="true">✓</span>
            </button>
          ))}
        </div>

        <div className="quiz-navigation">
          <button
            className="button button--ghost button--compact"
            disabled={draft.currentIndex === 0}
            onClick={() =>
              setDraft((current) => ({
                ...current,
                currentIndex: Math.max(0, current.currentIndex - 1),
              }))
            }
          >
            ← 上一题
          </button>
          {isLast ? (
            <button
              className="button button--primary button--compact"
              disabled={answeredCount !== questions.length}
              onClick={onSubmit}
            >
              查看我的历史知己
            </button>
          ) : (
            <span>选择后自动进入下一题</span>
          )}
        </div>
      </section>
    </main>
  );
}

function Submitting() {
  return (
    <main className="loading-stage" role="status" aria-live="polite">
      <div className="loading-orbit" aria-hidden="true">
        <span>鉴</span>
      </div>
      <p className="kicker">正在校准六面人格</p>
      <h1>从你的选择里，寻找跨越千年的回声</h1>
      <div className="loading-lines" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <small>正在比对 12 位历史人物画像…</small>
    </main>
  );
}

function DimensionProfile({ result }: { result: PublicResult }) {
  const poles = {
    decisiveness: ["审慎", "主动"],
    strategy: ["直觉", "推演"],
    idealism: ["现实", "理想"],
    influence: ["独立", "连接"],
    risk: ["稳健", "探索"],
    order: ["弹性", "秩序"],
  } as const;

  return (
    <div className="profile-bars">
      {DIMENSIONS.map((dimension) => {
        const value = result.normalizedScores[dimension];
        const width = Math.round(((value + 1) / 2) * 100);
        return (
          <div className="profile-bar" key={dimension}>
            <div>
              <span>{dimensionLabels[dimension]}</span>
              <small>
                {value > 0.2
                  ? `偏${poles[dimension][1]}`
                  : value < -0.2
                    ? `偏${poles[dimension][0]}`
                    : "相对平衡"}
              </small>
            </div>
            <div className="profile-bar__track">
              <span style={{ width: `${Math.max(8, width)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ResultPage({
  result,
  accessToken,
  onResultChange,
  onRefresh,
  onRetest,
}: {
  result: PublicResult;
  accessToken: string;
  onResultChange: (next: PublicResult) => void;
  onRefresh: () => Promise<void>;
  onRetest: () => void;
}) {
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState("");
  const [checkoutConfig, setCheckoutConfig] = useState<{
    amount: string;
    currency: string;
    environment: "test" | "prod";
  } | null>(null);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [awaitingPayment, setAwaitingPayment] = useState(
    () =>
      new URLSearchParams(window.location.search).get("payment") ===
      "success",
  );
  const [feedback, setFeedback] = useState("");
  const figure = result.figure;
  const checkoutPrice = checkoutConfig
    ? new Intl.NumberFormat("zh-CN", {
        style: "currency",
        currency: checkoutConfig.currency,
      }).format(Number(checkoutConfig.amount))
    : "";

  useEffect(() => {
    track(
      result.unlocked ? "full_report_viewed" : "free_result_viewed",
      { figureSlug: figure.slug },
      result.publicToken,
    );
  }, [figure.slug, result.publicToken, result.unlocked]);

  useEffect(() => {
    if (result.unlocked) {
      setAwaitingPayment(false);
      return;
    }
    if (!awaitingPayment) return;

    const refresh = () => {
      void onRefresh().catch(() => undefined);
    };
    refresh();
    const interval = window.setInterval(refresh, 3000);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
    };
  }, [awaitingPayment, onRefresh, result.unlocked]);

  useEffect(() => {
    if (result.unlocked) return;
    void fetch("/api/checkout")
      .then(async (response) => {
        if (!response.ok) return;
        const payload = (await response.json()) as {
          amount: string;
          currency: string;
          environment: "test" | "prod";
        };
        setCheckoutConfig(payload);
      })
      .catch(() => undefined);
  }, [result.unlocked]);

  const startCheckout = async () => {
    setStartingCheckout(true);
    setCheckoutError("");
    setCheckoutUrl("");
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ publicToken: result.publicToken }),
      });
      const payload = (await response.json()) as {
        checkoutUrl?: string;
        unlocked?: boolean;
        error?: string;
      };
      if (payload.unlocked) {
        await onRefresh();
        return;
      }
      if (!response.ok || !payload.checkoutUrl) {
        throw new Error(payload.error ?? "支付发起失败，请重试");
      }

      setAwaitingPayment(true);
      const opened = window.open(
        payload.checkoutUrl,
        "_blank",
        "noopener,noreferrer",
      );
      if (!opened) setCheckoutUrl(payload.checkoutUrl);
      track(
        "payment_checkout_started",
        { provider: "waffo", figureSlug: figure.slug },
        result.publicToken,
      );
    } catch (error) {
      setCheckoutError(
        error instanceof Error ? error.message : "支付发起失败，请重试",
      );
    } finally {
      setStartingCheckout(false);
    }
  };

  const redeem = async () => {
    setRedeeming(true);
    setRedeemError("");
    try {
      const response = await fetch("/api/redeem", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          publicToken: result.publicToken,
          code,
        }),
      });
      const payload = (await response.json()) as {
        result?: PublicResult;
        error?: string;
      };
      if (!response.ok || !payload.result) {
        throw new Error(payload.error ?? "兑换失败，请重试");
      }
      onResultChange(payload.result);
    } catch (error) {
      setRedeemError(
        error instanceof Error ? error.message : "兑换失败，请重试",
      );
    } finally {
      setRedeeming(false);
    }
  };

  const downloadShareCard = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1440;
    const context = canvas.getContext("2d");
    if (!context) return;

    const gradient = context.createLinearGradient(0, 0, 1080, 1440);
    gradient.addColorStop(0, "#f4eddf");
    gradient.addColorStop(0.62, "#e9deca");
    gradient.addColorStop(1, figure.color);
    context.fillStyle = gradient;
    context.fillRect(0, 0, 1080, 1440);

    context.globalAlpha = 0.08;
    context.fillStyle = "#2b2118";
    for (let x = -240; x < 1300; x += 180) {
      context.save();
      context.translate(x, 180);
      context.rotate(-0.45);
      context.fillRect(0, 0, 2, 1100);
      context.restore();
    }
    context.globalAlpha = 1;

    context.fillStyle = "#31261d";
    context.font = "500 34px serif";
    context.fillText("明镜 · 历史人格志", 90, 104);
    context.font = "500 30px sans-serif";
    context.fillStyle = "#6f6256";
    context.fillText("我的历史人物原型组合", 90, 232);

    context.beginPath();
    context.arc(540, 530, 205, 0, Math.PI * 2);
    context.fillStyle = figure.color;
    context.fill();
    context.beginPath();
    context.arc(540, 530, 176, 0, Math.PI * 2);
    context.strokeStyle = "rgba(255,255,255,.52)";
    context.lineWidth = 2;
    context.stroke();
    context.fillStyle = "#fffaf0";
    context.font = "700 190px serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(figure.monogram, 540, 520);

    context.fillStyle = "#31261d";
    context.font = "700 108px serif";
    context.fillText(figure.name, 540, 850);
    context.font = "500 30px sans-serif";
    context.fillStyle = "#6f6256";
    context.fillText(
      `${figure.era} · ${figure.role}｜次原型 ${result.reportPreview.secondaryName}`,
      540,
      910,
    );

    context.font = "500 41px serif";
    context.fillStyle = "#31261d";
    const words = figure.tagline.split("");
    const lines: string[] = [];
    let line = "";
    for (const word of words) {
      if (context.measureText(line + word).width > 760) {
        lines.push(line);
        line = word;
      } else {
        line += word;
      }
    }
    if (line) lines.push(line);
    lines.forEach((text, index) => context.fillText(text, 540, 1020 + index * 62));

    context.font = "500 28px sans-serif";
    figure.labels.forEach((label, index) => {
      const x = 250 + index * 290;
      context.fillStyle = "rgba(255,250,240,.84)";
      context.fillRect(x - 105, 1180, 210, 64);
      context.fillStyle = figure.color;
      context.fillText(label, x, 1222);
    });

    context.font = "500 26px sans-serif";
    context.fillStyle = "rgba(49,38,29,.68)";
    context.textAlign = "left";
    context.fillText("14 道选择，生成一份历史叙事", 90, 1350);
    context.textAlign = "right";
    context.fillText("娱乐内容 · 非心理测评", 990, 1350);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/png", 1),
    );
    if (!blob) return;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `明镜人格-${figure.name}.png`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    track(
      "share_card_downloaded",
      { figureSlug: figure.slug, unlocked: result.unlocked },
      result.publicToken,
    );
  };

  const sendFeedback = (value: string) => {
    setFeedback(value);
    track(
      result.unlocked ? "report_value_feedback" : "result_feedback",
      { value, figureSlug: figure.slug },
      result.publicToken,
    );
  };

  return (
    <main className="result-page">
      <header className="result-header">
        <BrandMark />
        <button className="text-button" onClick={onRetest}>
          重新测试
        </button>
      </header>

      <section
        className="result-hero"
        style={{ "--figure-color": figure.color } as React.CSSProperties}
      >
        <div className="result-hero__grain" aria-hidden="true" />
        <div className="result-hero__content">
          <p className="kicker">本次回答的历史人物原型</p>
          <FigureMedallion figure={figure} size="large" />
          <div className="result-hero__title">
            <span>主原型</span>
            <h1>{figure.name}</h1>
            <p>{figure.era} · {figure.role}</p>
          </div>
          <div className="archetype-mark">
            <strong>主</strong>
            <span>
              次原型
              <small>{result.reportPreview.secondaryName}</small>
            </span>
          </div>
          <blockquote>
            {result.reportPreview.archetypeName}
            <small>{figure.tagline}</small>
          </blockquote>
          <div className="result-tags">
            {figure.labels.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="result-body section-shell">
        <div className="result-grid">
          <article className="paper-card result-summary">
            <p className="card-index">01 · 核心判断</p>
            <h2>{result.reportPreview.title}</h2>
            <p className="lead-copy">{result.reportPreview.preview}</p>
            <p className="evidence-hint">{result.reportPreview.evidenceHint}</p>
            <div className="ink-rule" />
            <p className="micro-note">
              这是按固定规则生成的历史叙事，用来提供一种观察自己的角度。
            </p>
          </article>

          <article className="paper-card">
            <p className="card-index">02 · 六面人格</p>
            <h2>你的选择轮廓</h2>
            <DimensionProfile result={result} />
          </article>
        </div>

        {!result.unlocked ? (
          <section className="unlock-card">
            <div className="unlock-card__intro">
              <span className="unlock-card__eyebrow">
                根据本次 14 个选择生成 · 仅此结果可见
              </span>
              <h2>{result.reportPreview.archetypeName}</h2>
              <p>{result.reportPreview.evidenceHint}</p>
              <div className="personalized-proof">
                <strong>付费后你会看到</strong>
                <p>3 个关键选择如何形成判断，而不是只看通用人物介绍</p>
                <p>主原型与次原型的差异，以及你为什么更接近{figure.name}</p>
                <p>结合高低维度生成的盲点、行动建议与一周小实验</p>
              </div>
              <div className="locked-chapters" aria-hidden="true">
                {["选择证据", "双原型组合", "核心优势", "容易忽略", "决策方式", "行动清单"].map(
                  (label, index) => (
                    <span key={label}>
                      <small>{String(index + 1).padStart(2, "0")}</small>
                      {label}
                    </span>
                  ),
                )}
              </div>
            </div>
            <div className="redeem-panel">
              {checkoutConfig ? (
                <div className="payment-panel">
                  <p>Waffo Pancake 安全支付</p>
                  <h3>
                    {checkoutPrice} 解锁专属历史人格志
                  </h3>
                  <button
                    className="button button--primary button--wide"
                    disabled={startingCheckout}
                    onClick={startCheckout}
                  >
                    {startingCheckout
                      ? "正在打开收银台…"
                      : `${checkoutPrice} 解锁我的完整报告`}
                  </button>
                  {checkoutConfig.environment === "test" ? (
                    <small className="payment-mode">当前为测试支付环境</small>
                  ) : null}
                  {awaitingPayment ? (
                    <p className="payment-pending" role="status">
                      收银台已打开，付款成功后本页会自动解锁。
                    </p>
                  ) : null}
                  {checkoutError ? (
                    <p className="form-error" role="alert">{checkoutError}</p>
                  ) : null}
                  {checkoutUrl ? (
                    <a
                      className="checkout-fallback"
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      浏览器拦截了新窗口，点此继续支付
                    </a>
                  ) : null}
                </div>
              ) : null}
              {checkoutConfig ? (
                <div className="payment-divider"><span>或使用兑换码</span></div>
              ) : null}
              <p>已在其他渠道购买？</p>
              <h3>输入兑换码，解锁本次结果</h3>
              <label htmlFor="redemption-code">兑换码</label>
              <input
                id="redemption-code"
                autoComplete="one-time-code"
                placeholder="MJ-XXXX-XXXX-XXXX"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                onFocus={() =>
                  track(
                    "unlock_clicked",
                    { figureSlug: figure.slug },
                    result.publicToken,
                  )
                }
              />
              {redeemError ? (
                <p className="form-error" role="alert">{redeemError}</p>
              ) : null}
              <button
                className="button button--primary button--wide"
                disabled={!code.trim() || redeeming}
                onClick={redeem}
              >
                {redeeming ? "正在验证…" : "立即解锁完整报告"}
              </button>
            </div>
          </section>
        ) : result.report ? (
          <section className="full-report">
            <div className="full-report__heading">
              <span>已解锁</span>
              <p className="kicker">你的完整历史人格志</p>
              <h2>{result.report.archetypeName}</h2>
              <p>{result.report.subtitle}</p>
            </div>
            {result.report.evidence.length ? (
              <section className="report-evidence">
                <div>
                  <p className="card-index">你的选择证据</p>
                  <h3>这份报告为什么属于你</h3>
                </div>
                <div>
                  {result.report.evidence.map((item) => (
                    <article key={`${item.scene}-${item.choice}`}>
                      <span>{item.scene}</span>
                      <strong>“{item.choice}”</strong>
                      <p>{item.insight}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
            {[
              ["01", "为什么你像这个人物", result.report.why],
              ["02", "你的核心优势", result.report.strengths],
              ["03", "容易忽略的问题", result.report.blindspots],
              ["04", "行动和决策方式", result.report.decisions],
              ["05", "人际关系模式", result.report.relationships],
              ["06", "压力下的表现", result.report.pressure],
              ["07", "更容易舒展的场景", result.report.environment],
              ["08", `关于${figure.name}的一段历史`, result.report.story],
              ["09", `为什么主原型不是${result.reportPreview.secondaryName}`, result.report.secondaryComparison],
              ["10", "给现在的你一条建议", result.report.advice],
            ].map(([index, title, copy]) => (
              <article className="report-section" key={index}>
                <span>{index}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </div>
              </article>
            ))}
            <section className="action-list">
              <p className="card-index">未来 7 天</p>
              <h3>把结果变成三个小实验</h3>
              <div>
                {result.report.actions.map((action, index) => (
                  <article key={action}>
                    <span>0{index + 1}</span>
                    <p>{action}</p>
                  </article>
                ))}
              </div>
            </section>
          </section>
        ) : null}

        <article className="paper-card near-matches">
          <div>
            <p className="card-index">03 · 原型组合</p>
            <h2>你也带着他们的一部分</h2>
          </div>
          <div className="near-matches__list">
            {result.topMatches.map((match, index) => {
              const matchedFigure = figures.find(
                (candidate) => candidate.slug === match.slug,
              );
              if (!matchedFigure) return null;
              return (
                <div key={match.slug}>
                  <span>0{index + 1}</span>
                  <FigureMedallion figure={matchedFigure} size="small" />
                  <p>
                    <strong>{matchedFigure.name}</strong>
                    <small>{matchedFigure.role}</small>
                  </p>
                  <b>{index === 0 ? "主原型" : "近邻"}</b>
                </div>
              );
            })}
          </div>
        </article>

        <section className="share-section">
          <div>
            <p className="kicker">让结果成为一张作品</p>
            <h2>保存你的历史人格卡</h2>
            <p>1080 × 1440 高清图片，适合小红书笔记与手机相册。</p>
          </div>
          <div className="share-preview" style={{ "--figure-color": figure.color } as React.CSSProperties}>
            <small>我的历史人物原型组合</small>
            <FigureMedallion figure={figure} />
            <strong>{figure.name}</strong>
            <p>{result.reportPreview.archetypeName}</p>
          </div>
          <button
            className="button button--primary"
            onClick={downloadShareCard}
          >
            保存结果卡
            <span aria-hidden="true">↓</span>
          </button>
        </section>

        <section className="feedback-card">
          <p>{result.unlocked ? "这份完整报告，值吗？" : "这份结果，像你吗？"}</p>
          <div>
            {(result.unlocked
              ? ["很值", "比较值", "一般", "不太值"]
              : ["很像", "比较像", "一般", "不太像"]
            ).map((value) => (
              <button
                className={feedback === value ? "selected" : ""}
                key={value}
                onClick={() => sendFeedback(value)}
              >
                {value}
              </button>
            ))}
          </div>
          {feedback ? (
            <small>
              谢谢，你的反馈会帮助我们继续提高内容价值。
            </small>
          ) : null}
        </section>

        <p className="result-disclaimer">
          本测试仅供娱乐，不属于专业心理测评，不作为职业、医疗、投资或人生决策依据。
        </p>
      </section>
    </main>
  );
}

export function MingJingApp() {
  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<Phase>("intro");
  const [draft, setDraft] = useState<DraftState>(() => createDraft());
  const [result, setResult] = useState<PublicResult | null>(null);
  const [resultAccessToken, setResultAccessToken] = useState("");
  const [error, setError] = useState("");

  const hasDraft = useMemo(
    () => Object.keys(draft.answers).length > 0,
    [draft.answers],
  );

  const loadResult = useCallback(
    async (publicToken: string, accessToken: string) => {
      const response = await fetch(
        `/api/results?token=${encodeURIComponent(publicToken)}`,
        {
          headers: accessToken
            ? { authorization: `Bearer ${accessToken}` }
            : undefined,
        },
      );
      const payload = (await response.json()) as {
        result?: PublicResult;
        error?: string;
      };
      if (!response.ok || !payload.result) {
        throw new Error(payload.error ?? "没有找到这份结果");
      }
      setResult(payload.result);
      setResultAccessToken(accessToken);
      setPhase("result");
    },
    [],
  );

  useEffect(() => {
    const savedDraft = readDraft();
    setDraft(savedDraft);
    setMounted(true);
    track("page_view", { testVersion: TEST_VERSION });

    const parameters = new URLSearchParams(window.location.search);
    const publicToken = parameters.get("result");
    if (publicToken) {
      const accessToken =
        window.localStorage.getItem(`mingjing_access_${publicToken}`) ?? "";
      void loadResult(publicToken, accessToken).catch((loadError) => {
        setError(
          loadError instanceof Error ? loadError.message : "结果读取失败",
        );
      });
    }
  }, [loadResult]);

  useEffect(() => {
    if (mounted) saveDraft(draft);
  }, [draft, mounted]);

  const startFresh = () => {
    const next = createDraft();
    setDraft(next);
    saveDraft(next);
    setResult(null);
    setResultAccessToken("");
    setError("");
    setPhase("quiz");
    window.history.replaceState({}, "", window.location.pathname);
    track("test_start", { testVersion: TEST_VERSION });
  };

  const submit = async () => {
    setPhase("submitting");
    setError("");
    try {
      const response = await fetch("/api/results", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          answers: draft.answers,
          attemptToken: draft.attemptToken,
          accessToken: draft.accessToken,
          source: readSource(),
        }),
      });
      const payload = (await response.json()) as {
        result?: PublicResult;
        error?: string;
      };
      if (!response.ok || !payload.result) {
        throw new Error(payload.error ?? "结果生成失败，请重试");
      }

      setResult(payload.result);
      setResultAccessToken(draft.accessToken);
      window.localStorage.setItem(
        `mingjing_access_${payload.result.publicToken}`,
        draft.accessToken,
      );
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?result=${encodeURIComponent(payload.result.publicToken)}`,
      );
      setPhase("result");
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "结果生成失败，请重试",
      );
      setPhase("quiz");
    }
  };

  const retest = () => {
    track("retest_clicked", {}, result?.publicToken);
    startFresh();
  };

  const refreshResult = useCallback(async () => {
    if (!result) return;
    await loadResult(result.publicToken, resultAccessToken);
  }, [loadResult, result?.publicToken, resultAccessToken]);

  if (!mounted) {
    return <Submitting />;
  }

  return (
    <>
      {error ? (
        <div className="global-error" role="alert">
          <span>{error}</span>
          <button onClick={() => setError("")}>知道了</button>
        </div>
      ) : null}

      {phase === "intro" ? (
        <Intro
          hasDraft={hasDraft}
          onStart={startFresh}
          onContinue={() => {
            setPhase("quiz");
            track("test_start", { resumed: true });
          }}
        />
      ) : null}
      {phase === "quiz" ? (
        <Quiz
          draft={draft}
          setDraft={setDraft}
          onBackToIntro={() => setPhase("intro")}
          onSubmit={submit}
        />
      ) : null}
      {phase === "submitting" ? <Submitting /> : null}
      {phase === "result" && result ? (
        <ResultPage
          result={result}
          accessToken={resultAccessToken}
          onResultChange={setResult}
          onRefresh={refreshResult}
          onRetest={retest}
        />
      ) : null}
    </>
  );
}
